import { z } from "zod";

/**
 * The step timeline of an agent turn (openspec change `agent-turn`, spec `agent-steps`, D1–D5).
 * The server writes one `data-step` part per model step (`id: step-<n>`, replaced when the step
 * ends): its order, its start and end times from the API's clock, its kind, the tool calls it
 * made by name with the documents they touched, and, for a reasoning step, a thinking summary
 * written by ESI (never the model's reasoning, #412). Stored with the message; a client that
 * does not know the part ignores it.
 */
export const STEP_PART = "data-step";

/** The part ID of step `n` (1-based within the message, continuations included). */
export const stepPartId = (n: number) => `step-${n}`;

/**
 * What a step did, derived from its tool calls (D1). `other` is any tool this version does not
 * name (product help, feedback drafts), and any kind a newer server writes that this client
 * does not know.
 */
export const STEP_KINDS = [
  "thinking",
  "search",
  "read",
  "web",
  "wiki_write",
  "artefact",
  "code",
  "task",
  "memory",
  "question",
  "people",
  "answer",
  "other",
] as const;
export type StepKind = (typeof STEP_KINDS)[number];

const KNOWN_KINDS: ReadonlySet<string> = new Set(STEP_KINDS);

/** A kind, where an unknown value degrades to `other` (a newer server's kind). */
export const stepKindSchema = z
  .string()
  .transform((kind): StepKind => (KNOWN_KINDS.has(kind) ? (kind as StepKind) : "other"));

/** Most queries a step part keeps per call, and its document IDs per step. */
export const STEP_SUMMARY_MAX_CHARS = 500;
export const STEP_DOCUMENTS_MAX = 50;
/** The longest thinking summary (D2), and the shortest reasoning that gets one. */
export const THINKING_SUMMARY_MAX_CHARS = 240;
export const THINKING_SUMMARY_MIN_REASONING_CHARS = 200;

/** One tool call of a step: its name and its query (searches). */
export const stepCallSchema = z.object({
  toolCallId: z.string().max(200),
  name: z.string().max(100),
  /** The query of a search, as the model wrote it. */
  summary: z.string().max(STEP_SUMMARY_MAX_CHARS).optional(),
  /** How many tasks or memories a read returned (agent-turn D10, D12); never their text. */
  count: z.int().min(0).max(1000).optional(),
});
export type StepCall = z.infer<typeof stepCallSchema>;

/**
 * `pending` while the summary is being written, `done` with it, `fallback` when it failed the
 * checks (the client shows a fixed sentence), `off` when `conversations.thinkingSummary` is off
 * (the duration only).
 */
export const THINKING_STATES = ["pending", "done", "fallback", "off"] as const;
export type ThinkingState = (typeof THINKING_STATES)[number];

export const stepThinkingSchema = z.object({
  state: z
    .string()
    .transform(
      (s): ThinkingState =>
        (THINKING_STATES as readonly string[]).includes(s) ? (s as ThinkingState) : "fallback",
    ),
  /** Present when `state` is `done`: derived text with the turn's label at this step. */
  summary: z.string().max(THINKING_SUMMARY_MAX_CHARS).optional(),
  /** The reasoning's length in characters (never its text). */
  chars: z.int().min(0),
});
export type StepThinking = z.infer<typeof stepThinkingSchema>;

/** A source of a step as one viewer may see it (D3): readable now, with its title and mark. */
export const stepSourceSchema = z.object({
  documentId: z.string(),
  title: z.string(),
  classification: z.string().optional(),
});
export type StepSource = z.infer<typeof stepSourceSchema>;

export const stepPartDataSchema = z.object({
  n: z.int().min(1),
  /** ISO times from the API's clock; `endedAt` absent while the step runs. */
  startedAt: z.iso.datetime(),
  endedAt: z.iso.datetime().optional(),
  kind: stepKindSchema,
  calls: z.array(stepCallSchema).max(100).default([]),
  thinking: stepThinkingSchema.optional(),
  /**
   * The documents, attachments and wiki pages the step's tools handed to the model, by ID.
   * Stored only, never streamed (the live part carries titles); a read resolves them per
   * viewer into `sources` and `hidden` and drops them (D3).
   */
  documents: z.array(z.string().max(100)).max(STEP_DOCUMENTS_MAX).optional(),
  /** The model key that served the step. */
  model: z.string().max(200).optional(),
  /** The step's sources this viewer reads now, by the same rule as the sources row. */
  sources: z.array(stepSourceSchema).max(STEP_DOCUMENTS_MAX).optional(),
  /** How many of the step's sources this viewer cannot read now (never which). */
  hidden: z.int().min(0).optional(),
});
export type StepPartData = z.infer<typeof stepPartDataSchema>;

/** The tool names of each kind; any other tool is `other`. */
const TOOL_KINDS: Readonly<Record<string, StepKind>> = {
  search_knowledge: "search",
  search_attachments: "search",
  document_outline: "read",
  open_section: "read",
  attachment_outline: "read",
  open_attachment_section: "read",
  wiki_read: "read",
  wiki_tree: "read",
  wiki_changes: "read",
  web_search: "web",
  web_fetch: "web",
  run_code: "code",
  wiki_write: "wiki_write",
  propose_wiki_page: "wiki_write",
  wiki_reply: "wiki_write",
  create_artefact: "artefact",
  update_artefact: "artefact",
  ask_user: "question",
  list_tasks: "task",
  create_task: "task",
  update_task: "task",
  recall_memory: "memory",
  propose_memory: "memory",
  find_people: "people",
};

/** The kind of one tool call. */
export const toolStepKind = (name: string): StepKind => TOOL_KINDS[name] ?? "other";

/** Most specific first (D1: a step with several kinds keeps the most specific). */
const SPECIFICITY: readonly StepKind[] = [
  "question",
  "code",
  "task",
  "memory",
  "people",
  "wiki_write",
  "artefact",
  "web",
  "read",
  "search",
  "other",
  "answer",
  "thinking",
];

/**
 * The kind of a step from its tool calls and whether it wrote text: the most specific tool
 * kind, else `answer` for text, else `thinking`.
 */
export function stepKindOf(toolNames: readonly string[], wroteText: boolean): StepKind {
  const kinds = new Set<StepKind>(toolNames.map(toolStepKind));
  if (wroteText) {
    kinds.add("answer");
  }
  return SPECIFICITY.find((k) => kinds.has(k)) ?? "thinking";
}

/** The `data-step` parts of a message, parsed; malformed ones are skipped. */
export function stepsOf(parts: readonly unknown[]): StepPartData[] {
  const steps: StepPartData[] = [];
  for (const part of parts) {
    if ((part as { type?: unknown } | null)?.type !== STEP_PART) {
      continue;
    }
    const parsed = stepPartDataSchema.safeParse((part as { data?: unknown }).data);
    if (parsed.success) {
      steps.push(parsed.data);
    }
  }
  return steps.sort((a, b) => a.n - b.n);
}

/** Kinds whose consecutive steps are one row ("Read 3 documents", "Searched sources"). */
const GROUPED: ReadonlySet<StepKind> = new Set(["search", "read", "web"]);

/** One row of the expanded timeline (D4). */
export interface TimelineRow {
  kind: StepKind;
  /** A thinking row of a step that also did something else, or of a thinking-only step. */
  thinking?: StepThinking;
  /** The steps the row covers (several for grouped reads and searches). */
  steps: readonly StepPartData[];
  /** Milliseconds, absent while a step of the row runs or when unknown (D5). */
  durationMs?: number;
  /** Queries of the row's searches. */
  queries: string[];
  sources: StepSource[];
  hidden: number;
  /** Documents touched, readable or not (for "Read 3 documents"). */
  documentCount: number;
  running: boolean;
}

const ms = (iso: string | undefined) => (iso ? Date.parse(iso) : Number.NaN);

function spanOf(steps: readonly StepPartData[]): number | undefined {
  const first = steps[0];
  const last = steps.at(-1);
  if (!first || !last?.endedAt) {
    return undefined;
  }
  const span = ms(last.endedAt) - ms(first.startedAt);
  return Number.isFinite(span) && span >= 0 ? span : undefined;
}

function documentCount(steps: readonly StepPartData[]): number {
  const seen = new Set<string>();
  let resolved = 0;
  for (const step of steps) {
    for (const source of step.sources ?? []) {
      seen.add(source.documentId.toLowerCase());
    }
    resolved += step.hidden ?? 0;
    for (const id of step.documents ?? []) {
      seen.add(id.toLowerCase());
    }
  }
  return seen.size + resolved;
}

/**
 * The rows of a timeline (D4, D5): a thinking row for each step with a summary record (its
 * duration only when the step did nothing else), then the step's own row, consecutive searches,
 * reads and web calls merged into one row. A thinking-only step without a summary record (short
 * reasoning) shows no row.
 */
export function timelineRows(steps: readonly StepPartData[]): TimelineRow[] {
  const rows: TimelineRow[] = [];
  for (const step of steps) {
    const running = step.endedAt === undefined;
    // A step still reasoning has no summary record yet: its live row shows it thinking.
    if (step.thinking || (step.kind === "thinking" && running)) {
      const only = step.kind === "thinking";
      rows.push({
        kind: "thinking",
        ...(step.thinking && { thinking: step.thinking }),
        steps: [step],
        ...(only && !running && { durationMs: spanOf([step]) }),
        queries: [],
        sources: [],
        hidden: 0,
        documentCount: 0,
        running: only && running,
      });
    }
    if (step.kind === "thinking") {
      continue;
    }
    const previous = rows.at(-1);
    const queries = step.calls.flatMap((c) => (c.summary ? [c.summary] : []));
    if (previous && previous.kind === step.kind && GROUPED.has(step.kind) && !previous.thinking) {
      const merged = [...previous.steps, step];
      const sources = [...previous.sources];
      for (const s of step.sources ?? []) {
        if (!sources.some((x) => x.documentId.toLowerCase() === s.documentId.toLowerCase())) {
          sources.push(s);
        }
      }
      rows[rows.length - 1] = {
        ...previous,
        steps: merged,
        durationMs: running ? undefined : spanOf(merged),
        queries: [...previous.queries, ...queries],
        sources,
        hidden: previous.hidden + (step.hidden ?? 0),
        documentCount: documentCount(merged),
        running,
      };
      continue;
    }
    const row: TimelineRow = {
      kind: step.kind,
      steps: [step],
      queries,
      sources: [...(step.sources ?? [])],
      hidden: step.hidden ?? 0,
      documentCount: documentCount([step]),
      running,
    };
    const duration = running ? undefined : spanOf([step]);
    rows.push(duration === undefined ? row : { ...row, durationMs: duration });
  }
  return rows;
}

/** The whole turn's duration: the first step's start to the last step's end (D5). */
export function turnDurationMs(steps: readonly StepPartData[]): number | undefined {
  return steps.length > 0 && steps.every((s) => s.endedAt !== undefined)
    ? spanOf(steps)
    : undefined;
}
